import { selectorWithoutCache as selector } from 'util/RecoilWithoutCache'
import { Decimal } from 'decimal.js'
import { calOrderOpenCost, MixContractFixedCurrentOrdersSelector } from './Order'
import {
    MixContractBaseFixedPositionListSelector,
    MixContractPositionStatisticSelector
} from './Position'
import { Zero } from 'helper/formula/MixContractFormula'
import { generateUniKey, getFixedUserAccountByTokenId, judgeOutput } from './Account'
import {
    MixContractDelegateType,
    MixContractHoldSide,
    MixContractMarginMode
} from 'constants/MixContractGlobal'
import { getMixContractInfoBySymbolId } from './Contract'
import { getTokenPriceByTokenId } from './Token'
import { decimal2Num } from 'util/price'
import StatisticResult from 'util/StatisticResult'

/**
 * 返回某订单是否为某方向的开仓订单
 * @param delegateType
 * @param marginMode
 * @param holdSide
 */
export function isOpenOrder(
    delegateType: MixContractDelegateType,
    marginMode: MixContractMarginMode,
    holdSide: MixContractHoldSide
): boolean {
    if (delegateType === MixContractDelegateType.OPEN_LONG) {
        if (marginMode === MixContractMarginMode.CROSS_MARGIN) {
            return true
        } else {
            return holdSide === MixContractHoldSide.LONG_POSITIONS
        }
    } else if (delegateType === MixContractDelegateType.OPEN_SHORT) {
        if (marginMode === MixContractMarginMode.CROSS_MARGIN) {
            return true
        } else {
            return holdSide === MixContractHoldSide.SHORT_POSITIONS
        }
    }

    return false
}

/**
 * 仅用于计算可用
 * 缺少维护看起来好像不对了
 * @deprecated use getFixedUserAccountByTokenId(tokenId) instead
 */
export const getCanUseCalculator = selector({
    key: 'can-use-calculator',
    get: ({ get }) => {
        const positionList = get(MixContractBaseFixedPositionListSelector)
        const orderList = get(MixContractFixedCurrentOrdersSelector)
        const { tokenTotalCrossedPositionUnrealizedProfit } = get(
            MixContractPositionStatisticSelector
        )

        return (
            symbolId: string,
            tokenId: string,
            leverage: Decimal.Value,
            marginMode: MixContractMarginMode,
            holdSide: MixContractHoldSide
        ) => {
            let contractConfig = get(getMixContractInfoBySymbolId(symbolId))
            let bp = get(getTokenPriceByTokenId(tokenId)).indexPrice
            const account = get(getFixedUserAccountByTokenId(tokenId))
            const totalCrossedUpnl = tokenTotalCrossedPositionUnrealizedProfit.get(tokenId)

            // 只计算当前币种的开仓成本之和
            let totalOpenCost = orderList.reduce<Decimal>((total, order) => {
                let dFreeze = Zero
                // 某保证金下的所有委托都要统计
                if (order.tokenId === tokenId) {
                    dFreeze = order.dFreeze
                    // 某币种，某合约下，某个方向的订单，需要重新计算委托成本
                    if (
                        order.symbolId === symbolId &&
                        isOpenOrder(order.delegateType, marginMode, holdSide)
                    ) {
                        dFreeze = calOrderOpenCost(order, bp, leverage, contractConfig)
                    }
                }
                return total.add(dFreeze)
            }, Zero)

            // 当前币种所有全仓保证金情况（额外追加逐仓需要追加的量）
            let totalMargin = positionList.reduce((total, position) => {
                let dMargin = Zero
                // 某保证金下的所有仓位都要考虑
                if (position.tokenId === tokenId) {
                    if (position.symbolId === symbolId) {
                        // 如果修改的是全仓杠杆
                        if (marginMode === MixContractMarginMode.CROSS_MARGIN) {
                            // 全仓仓位重新衡量保证金
                            dMargin = position.dOpenValue.div(leverage)
                        } else {
                            // 逐仓杠杆区分方向，计算需要追加的部分
                            if (holdSide === position.holdSide) {
                                let diff = position.dMargin.sub(position.dOpenValue.div(leverage))
                                if (diff.greaterThan(Zero)) {
                                    // 如果逐仓仓位需要追加
                                    dMargin = diff
                                }
                            }
                        }
                    } else {
                        // 非当前交易对的不用重新计算
                        if (position.marginMode === MixContractMarginMode.CROSS_MARGIN) {
                            dMargin = position.dMargin
                        }
                    }
                }
                return total.add(dMargin)
            }, Zero)
            let dCanUse = new Decimal(account.available)
                .sub(totalOpenCost)
                .sub(totalMargin)
                .add(totalCrossedUpnl)
            let dCanUseValue = dCanUse.times(bp)

            return { dCanUse, dCanUseValue }
        }
    }
})

/**
 * 计算补偿保证金
 *
 * @param longCurPosMargin: 多头当前仓位保证金
 * @param longOrderMargin: 多头委托所需保证金
 * @param holdSide: 持仓方向
 * @param offsetMargin: 占用保证金
 * @param currOrderFreezeD: 当前委托单的冻结资金 默认为0 传入代表计算撤销当前委托单后的补偿保证金
 * @param shortCurPosMargin: 空头当前仓位保证金
 * @param shortOrderMargin: 空头委托所需保证金
 *
 * */

export const calcCompensateMargin = ({
    longCurPosMargin,
    longPosMargin,
    longOrderMargin,
    holdSide,
    offsetMargin,
    currOrderFreezeUSDTD = Zero,
    shortCurPosMargin,
    shortPosMargin,
    shortOrderMargin
}) => {
    /**
     *
     * 该币对多头委托所需保证金(new) = 该币对多头委托所需保证金 - A
     * 该币对占用保证金(new) = offsetMargin
     *
     * judgeOutput(a-b,1,-1)这个的意思是如果a-b>0,结果为1，否则为-1 代码中使用 judgeOutput 方法即可
     *
     * 多头委托订单
     * - 该币对多头补偿保证金(new) = judgeOutput(
     * 该币对多头当前仓位保证金 + 该币对多头委托所需保证金(new) - 该币对空头当前仓位保证金 - 该币对空头委托所需保证金,
     * 该币对占用保证金(new) - 该币对多头仓位保证金 - 该币对多头委托所需保证金(new),
     * 该币对占用保证金(new) +该币对当前空头仓位保证金 - 该币对多头当前仓位保证金 - 该币对多头委托所需保证金(new))
     *
     * - 该币对空头补偿保证金(new) = judgeOutput(
     * 该币对空头当前仓位保证金 + 该币对空头委托所需保证金 - 该币对多头当前仓位保证金 - 该币对多头委托所需保证金(new),
     * 该币对占用保证金(new) - 该币对空头仓位保证金 - 该币对空头委托所需保证金,
     * 该币对占用保证金 (new)+ 该币对多头当前仓位保证金 - 该币对空头当前仓位保证金 - 该币对空头委托所需保证金)
     *
     * 空头委托订单
     * - 该币对多头补偿保证金(new) = judgeOutput(
     * 该币对多头当前仓位保证金 + 该币对多头委托所需保证金 - 该币对空头当前仓位保证金 - 该币对空头委托所需保证金(new),
     * 该币对占用保证金(new) - 该币对多头仓位保证金 - 该币对多头委托所需保证金,
     * 该币对占用保证金(new) +该币对当前空头仓位保证金 - 该币对多头当前仓位保证金 - 该币对空头委托所需保证金)
     *
     * - 该币对空头补偿保证金(new) = judgeOutput(该币对空头当前仓位保证金 + 该币对空头委托所需保证金(new) - 该币对多头当前仓位保证金 - 该币对多头委托所需保证金,
     * 该币对占用保证金(new) - 该币对空头仓位保证金 - 该币对空头委托所需保证金(new),
     * 该币对占用保证金 (new)+ 该币对多头当前仓位保证金 - 该币对空头当前仓位保证金 - 该币对空头委托所需保证金(new))
     *
     * currOrderFreezeUSDTD: A
     * */
    let sameSideCurPosMargin,
        sameSidePosMargin,
        sameSideOrderMargin,
        oppositeSideCurPosMargin,
        oppositeSidePosMargin,
        oppositeSideOrderMargin,
        sameSideOrderMarginSubFreeze

    if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
        sameSideCurPosMargin = longCurPosMargin
        sameSideOrderMargin = longOrderMargin
        sameSidePosMargin = longPosMargin
        oppositeSideCurPosMargin = shortCurPosMargin
        oppositeSideOrderMargin = shortOrderMargin
        oppositeSidePosMargin = shortPosMargin
    } else {
        sameSideCurPosMargin = shortCurPosMargin
        sameSideOrderMargin = shortOrderMargin
        sameSidePosMargin = shortPosMargin
        oppositeSideCurPosMargin = longCurPosMargin
        oppositeSideOrderMargin = longOrderMargin
        oppositeSidePosMargin = longPosMargin
    }

    sameSideOrderMarginSubFreeze = sameSideOrderMargin.sub(currOrderFreezeUSDTD) //  同向委托所需保证金（new）

    /**
     * 同向补偿保证金 = judgeOutput(
     *     同向当前仓位保证金 + 同向委托所需保证金（new） - 反向当前仓位保证金 - 反向委托所需保证金,
     *     占用保证金(new) - 同向仓位保证金 - 同向委托所需保证金(new),
     *     占用保证金(new) + 反向当前仓位保证金 - 同向当前仓位保证金 - 同向委托所需保证金(new)
     * )
     * */

    const sameSideCompensateMargin = judgeOutput(
        sameSideCurPosMargin
            .add(sameSideOrderMarginSubFreeze)
            .sub(oppositeSideCurPosMargin)
            .sub(oppositeSideOrderMargin),
        offsetMargin.sub(sameSidePosMargin).sub(sameSideOrderMarginSubFreeze),
        offsetMargin
            .add(oppositeSideCurPosMargin)
            .sub(sameSideCurPosMargin)
            .sub(sameSideOrderMarginSubFreeze)
    )

    /**
     * 反向补偿保证金 = judgeOutput(
     *     反向当前仓位保证金 + 反向委托所需保证金(new) - 同向当前仓位保证金 - 同向委托所需保证金,
     *     占用保证金(new) - 反向仓位保证金 - 反向委托所需保证金(new),
     *     占用保证金(new) + 同向当前仓位保证金 - 反向当前仓位保证金 - 反向委托所需保证金(new)
     * )
     * */
    const oppositeSideCompensateMargin = judgeOutput(
        oppositeSideCurPosMargin
            .add(oppositeSideOrderMargin)
            .sub(sameSideCurPosMargin)
            .sub(sameSideOrderMarginSubFreeze),
        offsetMargin.sub(oppositeSidePosMargin).sub(oppositeSideOrderMargin),
        offsetMargin
            .add(sameSideCurPosMargin)
            .sub(oppositeSideCurPosMargin)
            .sub(oppositeSideOrderMargin)
    )

    if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
        return {
            longCompensateMargin: sameSideCompensateMargin,
            shortCompensateMargin: oppositeSideCompensateMargin
        }
    } else {
        return {
            longCompensateMargin: oppositeSideCompensateMargin,
            shortCompensateMargin: sameSideCompensateMargin
        }
    }
}

//计算多头最大可开 里面区分全仓逐仓
export const calcalteLongNewDCanUseValue = (
    marginMode,
    canUseValue,
    compensateMargin,
    posNum,
    fairPrice,
    tokenTotalUpnl,
    fee,
    bp
) => {
    let res

    //逐仓
    if (marginMode === MixContractMarginMode.FIXED_MARGIN) {
        let needSub = new Decimal(posNum).times(new Decimal(fairPrice)).times(new Decimal(fee)) // 收取的手续费
        let newCanUse = canUseValue.add(compensateMargin).sub(needSub).add(tokenTotalUpnl.times(bp))
        res = newCanUse
    } else {
        let needSub = new Decimal(posNum).times(new Decimal(fairPrice)).times(new Decimal(fee))
        let newCanUse = canUseValue.add(compensateMargin).sub(needSub)
        res = newCanUse
    }

    return res
}
interface getSingleSideModifyMaxOptionsI {
    tokenId: string
    symbolId: string

    holdSide: MixContractHoldSide
    tokenLongTotalUpnl: StatisticResult
    tokenShortTotalUpnl: StatisticResult
    canUseWithDFreeze: Decimal
    marginMode: MixContractMarginMode
    bp: string
    feeRate: any
    markPrice: string
    shortPosNum: Decimal
    longPosNum: Decimal
    /** currOrderFreezeD：当前委托单的冻结资金
     * 如果是在算撤单后的可用资金，需要传入当前委托单的冻结资金
     * 同时注意 offsetMargin 需要重新计算
     * offsetMargin = max(同方向仓位保证金 + 同方向委托保证金 - A， 反方向仓位保证金 + 反方向委托保证金)
     * */
    currOrderFreezeD?: Decimal
    longCompensateMargin: Decimal
    shortCompensateMargin: Decimal
}

/**
 * 计算单项持仓的可用资金
 * @param {string} tokenId 币种id
 * @param {string} symbolId 合约id
 * @param {Decimal} offsetMargin 该币对占用保证金
 // * @param {Decimal} longPosMargin 单币对多头仓位保证金
 * @param {Decimal} longOrderMargin 单币对多头委托保证金
 * @param {Decimal} longCurPosMargin 单币对当前多头仓位保证金
 // * @param {Decimal} shortPosMargin 单币对空头仓位保证金（dMargin）
 * @param {Decimal} shortOrderMargin 该币对空头委托所需保证金
 * @param {Decimal} shortCurPosMargin 单币对当前空头仓位保证金（dMargin4CalcCanOpen）
 * @param {string} markPrice 当前标记价格
 * @param {Decimal} shortPosNum 当前币对空头仓位数量
 * @param {Decimal} longPosNum 当前币对多头仓位数量
 * @param {MixContractHoldSide} holdSide 持仓方向
 * @param {StatisticResult} tokenLongTotalUpnl 多头总未实现盈亏
 * @param {StatisticResult} tokenShortTotalUpnl 空头总未实现盈亏
 * @param {Decimal} canUseValue 可用
 * @param {MixContractMarginMode} marginMode 保证金模式
 * @param {string} feeRate 手续费率
 * @param {string} bp 基点
 * @param {Decimal} currOrderFreezeD 当前委托单的冻结资金
 * 如果是在算撤单后的可用资金，需要传入当前委托单的冻结资金
 * 同时注意 offsetMargin 需要重新计算
 * offsetMargin = max(同方向仓位保证金 + 同方向委托保证金 - A， 反方向仓位保证金 + 反方向委托保证金)
 *
 * @returns {Decimal} 返回计算后的单项持仓的可用资金
 */
export const calcSingleSideCanUse = ({
    tokenId,
    symbolId,
    longCompensateMargin,
    shortCompensateMargin,
    markPrice,
    shortPosNum,
    longPosNum,
    holdSide,
    tokenLongTotalUpnl,
    tokenShortTotalUpnl,
    canUseWithDFreeze, // 这个参数？
    marginMode,
    feeRate,
    bp
}: getSingleSideModifyMaxOptionsI) => {
    const unionKey = generateUniKey(tokenId, symbolId)
    let newDCanUseValue

    if (holdSide === MixContractHoldSide.LONG_POSITIONS) {
        newDCanUseValue = calcalteLongNewDCanUseValue(
            marginMode,
            canUseWithDFreeze,
            longCompensateMargin,
            shortPosNum,
            markPrice,
            tokenShortTotalUpnl.get(unionKey),
            feeRate,
            bp
        )
    } else {
        newDCanUseValue = calcalteLongNewDCanUseValue(
            marginMode,
            canUseWithDFreeze,
            shortCompensateMargin,
            longPosNum,
            markPrice,
            tokenLongTotalUpnl.get(unionKey),
            feeRate,
            bp
        )
    }

    return newDCanUseValue
}
